/*
 *  Money formatting.
 *
 *  All money crosses the wire as a decimal STRING (the backend serialises
 *  Numeric that way to avoid float drift), so every helper here takes a
 *  string and guards against NaN by falling back to the raw input rather
 *  than printing "NaN".
 *
 *  A GEL-valued field can be `null` when the FX cache was cold at write time
 *  and no rate could be resolved — that is meaningfully different from zero,
 *  so it renders as an em-dash.
 *
 *  Note: `Deals.tsx`, `Placements.tsx`, `MarketShared.tsx` and several other
 *  pages still carry their own module-scoped copies of the currency-symbol
 *  ternary. This module is where they should converge; it is used by the
 *  Finance page today.
 */

export const CURRENCY_SYMBOL: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GEL: "₾",
};

const DASH = "—";

export function currencySymbol(currency: string): string {
  return CURRENCY_SYMBOL[currency?.toUpperCase()] ?? "";
}

/** `₾12,345` — no decimals. For headline figures and KPI tiles. */
export function fmtGel(amount: string | null | undefined): string {
  if (amount === null || amount === undefined) return DASH;
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  return `₾${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/** `$1,200.50` — exact, up to 2dp, symbol-prefixed. Unknown currency codes
 *  fall back to a trailing code so the number is never shown bare. */
export function fmtAmount(
  amount: string | null | undefined,
  currency: string,
): string {
  if (amount === null || amount === undefined) return DASH;
  const n = Number(amount);
  const body = Number.isFinite(n)
    ? n.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : amount;
  const sym = currencySymbol(currency);
  return sym ? `${sym}${body}` : `${body} ${currency}`;
}

/** Exact, never abbreviated, rounded to whole units. Use where a figure is
 *  read as an accounting value — a ₾12,400 salary must not print as "₾12k". */
export function fmtMoneyFull(
  amount: string | null | undefined,
  currency: string,
): string {
  if (amount === null || amount === undefined) return DASH;
  const n = Number(amount);
  if (!Number.isFinite(n)) return amount;
  return `${currencySymbol(currency)}${Math.round(n).toLocaleString()}`;
}

/** `+$1,200` / `−₾900`. Uses U+2212 MINUS SIGN, which aligns with digits in
 *  a tabular-nums column where the ASCII hyphen does not. */
export function fmtSigned(
  amount: string | null | undefined,
  currency: string,
  direction: "in" | "out",
): string {
  const body = fmtAmount(amount, currency);
  if (body === DASH) return DASH;
  return `${direction === "in" ? "+" : "−"}${body}`;
}

/** Signed GEL, for the net column. */
export function fmtSignedGel(
  amount: string | null | undefined,
  direction: "in" | "out",
): string {
  const body = fmtGel(amount);
  if (body === DASH) return DASH;
  return `${direction === "in" ? "+" : "−"}${body}`;
}
